import { Heading, Card, Columns, Column, Stack, breakpoints, Inline, Tooltip } from '@etg/wings';
import { InformationIconButton, Notification, variants } from '@eti/components';
import { useProperty, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { FormSection } from 'redux-form';
import { CONTACT_INFO_FORM_SECTION, contactFields } from '../../constants/formFields';
import type { Country } from '../../models';
import Email from './Email';
import EmailConfirm from './EmailConfirm';
import LastName from './LastName';
import Newsletter from './Newsletter';
import type { PhoneType } from './Phone';
import Phone from './Phone';
import TravelArranger from './TravelArranger';

const { EMERGENCY_PHONE, EMERGENCY_PHONE_COUNTRY_ID, PHONE, PHONE_COUNTRY_ID } = contactFields;

const fieldsetStyles = css`
  border: none;
`;

const contactInfoTooltipTriggerStyles = css`
  cursor: pointer;

  @media (min-width: ${breakpoints._768}) {
    cursor: default;
  }
`;

const columnWidth = { [breakpoints._0]: '4/4', [breakpoints._768]: '2/4' } as const;

interface ContactFormProps {
  countries: Country[];
  form: string;
  hasAsyncEmergencyPhoneError?: boolean;
  hasAsyncPhoneError?: boolean;
  onCountryChange: (phoneType: PhoneType) => void;
  shouldShowEmergencyContactInformation?: boolean;
}

const ContactForm = ({
  countries,
  form,
  hasAsyncEmergencyPhoneError = false,
  hasAsyncPhoneError = false,
  onCountryChange,
  shouldShowEmergencyContactInformation = false,
}: ContactFormProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const consentInformationText = t('TD.Contact.Legal.Consent');

  return (
    <Card data-contactinfoscrollanchor data-testid="travelerDetails-contactForm">
      <FormSection name={CONTACT_INFO_FORM_SECTION}>
        <fieldset
          aria-labelledby="contactForm-title"
          className={cx(fieldsetStyles, 'contactInformationSection')}
        >
          <Card.Section>
            <Stack>
              <Inline alignY="center" noWrap spacing={8}>
                <Heading id="contactForm-title" level={2}>
                  {t('ContactForm.Header')}
                </Heading>
                <Tooltip
                  content={t('ContactForm.Email.HintLabel')}
                  data-testid="contact-information-tooltip"
                  title={t('ContactForm.Header')}
                  trigger="click"
                >
                  <InformationIconButton
                    className={contactInfoTooltipTriggerStyles}
                    data-testid="contact-information-icon"
                  />
                </Tooltip>
              </Inline>
              {consentInformationText && <div>{consentInformationText}</div>}
              {p('TravelerDetails.EmailField.BlockedDomains.Enabled') && (
                <Notification className="info" variant={variants.INFORMATION}>
                  {t('TravelerDetails.EmailBlock.Text')}
                </Notification>
              )}
              <Stack spacing={24}>
                {p('TravelerDetails.TravelArranger.Enabled') && <TravelArranger form={form} />}
                <Columns spacing={24}>
                  <Column width={columnWidth}>
                    <Email />
                  </Column>
                  {p('IbeClient.ContactInformation.Email.ShowConfirmEmail') && (
                    <Column width={columnWidth}>
                      <EmailConfirm />
                    </Column>
                  )}
                  <Column width={columnWidth}>
                    <Phone
                      countries={countries}
                      countryCodeLabel={t('ContactForm.PhoneCountryCode.Label')}
                      data-testid="traveler-phone-input"
                      description={t('ContactForm.Phone.HintLabel')}
                      form={form}
                      formSection={CONTACT_INFO_FORM_SECTION}
                      hasAsyncError={hasAsyncPhoneError}
                      id={PHONE}
                      label={t('ContactForm.Phone.Label')}
                      name={PHONE}
                      onCountryChange={onCountryChange}
                      phoneCountryId={PHONE_COUNTRY_ID}
                      placeholder={t('Input.Phone.Placeholder')}
                      shouldMarkInputAsRequired
                    />
                  </Column>
                  {p('TD.NewsletterCheckbox.Show.Enabled') && (
                    <Column width="4/4">
                      <Newsletter />
                    </Column>
                  )}
                </Columns>
              </Stack>
            </Stack>
          </Card.Section>
          {shouldShowEmergencyContactInformation && (
            <Card.Section>
              <fieldset aria-labelledby="emergencyForm-title" className={fieldsetStyles}>
                <Stack data-testid="ContactForm-EmergencyContact">
                  <Heading id="emergencyForm-title" level={3}>
                    {t('ContactForm.EmergencyContact.Title')}
                  </Heading>
                  <Columns spacing={24}>
                    <Column width={columnWidth}>
                      <LastName />
                    </Column>
                    <Column width={columnWidth}>
                      <Phone
                        countries={countries}
                        countryCodeLabel={t('ContactForm.EmergencyContact.PhoneCountryCode.Label')}
                        data-testid="traveler-emergency-phone-input"
                        description={t('ContactForm.EmergencyContact.Phone.HintLabel')}
                        form={form}
                        formSection={CONTACT_INFO_FORM_SECTION}
                        hasAsyncError={hasAsyncEmergencyPhoneError}
                        id={EMERGENCY_PHONE}
                        label={t('ContactForm.EmergencyContact.Phone.Label')}
                        name={EMERGENCY_PHONE}
                        onCountryChange={onCountryChange}
                        phoneCountryId={EMERGENCY_PHONE_COUNTRY_ID}
                        placeholder={t('ContactForm.EmergencyContact.Phone.Placeholder')}
                      />
                    </Column>
                  </Columns>
                </Stack>
              </fieldset>
            </Card.Section>
          )}
        </fieldset>
      </FormSection>
    </Card>
  );
};

export default ContactForm;
